/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useExtensionData.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for loading extension data and options.
    Options loading → useExtensionOptions
    Mapping logic  → extensionMapper.ts
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchExtensionById, fetchSystemDefaults, fetchNextExtensionNumber } from '../api/extensionsApi';
import { useContextStore } from '../../../store/context';
import type {
    ExtensionFormState,
    VoicemailSettingsState,
    RecordingSettingsState,
    ContactInfoState,
    FollowMeSettingsState,
    PersonalAssistantState,
} from '../types';
import {
    createDefaultFormData,
    createDefaultVoicemailData,
    createDefaultRecordingSettings,
    createDefaultContactInfo,
    createDefaultFollowMeData,
    createDefaultPersonalAssistantData,
} from '../utils/formDefaults';
import { parseLanguagePayload, buildSoundPrefixPath, getDefaultVoicemailLanguage } from '../utils/languageHelpers';
import { DEFAULT_LANGUAGE_CODE } from '../../../data/formOptions';
import {
    mapExtensionToFormData,
    mapExtensionToDeviceData,
    mapExtensionToVoicemail,
    mapExtensionToRecordingSettings,
    mapExtensionToContactInfo,
    mapExtensionToFollowMe,
    mapExtensionToPersonalAssistant,
} from '../utils/extensionMapper';
import { useExtensionOptions } from './useExtensionOptions';

interface SystemDefaults {
    defaultLanguage?: string;
    defaultVoice?: string;
    voicemailEnabled?: boolean;
    ringTimeout?: number;
    maxCalls?: number;
    callWaiting?: boolean;
    rejectAnonymous?: boolean;
    holdMusic?: string;
    privacy?: string;
    callScreen?: boolean;
    maxRegistrations?: number;
    inboundCodecPrefs?: string;
    outboundCodecPrefs?: string;
    dtmfMode?: string;
    voicemailMaxMessages?: number;
    voicemailNotificationEnabled?: boolean;
    voicemailTranscribeEnabled?: boolean;
    voicemailEmailAttachment?: boolean;
    defaultTimezone?: string;
    recordIncoming?: boolean;
    recordOutgoing?: boolean;
    recordInternal?: boolean;
    recordOnDemand?: boolean;
    recordingFormat?: string;
}

interface UseExtensionDataOptions {
    id?: string;
    duplicateId?: string | null;
    isEditMode: boolean;
    selectedDomainId?: number;
}

export function useExtensionData({
    id,
    duplicateId,
    isEditMode,
    selectedDomainId,
}: UseExtensionDataOptions) {
    const location = useLocation();

    const [formData, setFormData] = useState<ExtensionFormState>(createDefaultFormData);
    const [voicemailData, setVoicemailData] = useState<VoicemailSettingsState>(createDefaultVoicemailData);
    const [recordingSettings, setRecordingSettings] = useState<RecordingSettingsState>(createDefaultRecordingSettings);
    const [contactInfo, setContactInfo] = useState<ContactInfoState>(createDefaultContactInfo);
    const [followMeData, setFollowMeData] = useState<FollowMeSettingsState>(createDefaultFollowMeData);
    const [personalAssistantData, setPersonalAssistantData] = useState<PersonalAssistantState>(createDefaultPersonalAssistantData);
    const [isLoading, setIsLoading] = useState(false);

    const lastLoadedIdRef = useRef<string | null>(null);
    const lastLocationKeyRef = useRef<string>('');

    // ── Options (delegated) ───────────────────────────────────
    const { options, isLoadingOptions } = useExtensionOptions(
        isEditMode,
        selectedDomainId,
        (cosId) => setFormData(prev => ({ ...prev, classOfServicesId: cosId as string | number | null })),
        (dpId) => setFormData(prev => ({ ...prev, dialProfileId: dpId as string | number | null })),
    );

    // ── Load / map extension from API ────────────────────────
    const loadExtension = useCallback(async (extensionId: string, duplicate = false) => {
        setIsLoading(true);
        try {
            const ext = await fetchExtensionById(extensionId);
            const { languageCode: normalizedLanguageCode } = parseLanguagePayload(ext.language_code);

            setFormData(mapExtensionToFormData(ext, duplicate));

            const vmData = mapExtensionToVoicemail(ext, normalizedLanguageCode);
            if (vmData) setVoicemailData(vmData);

            setRecordingSettings(mapExtensionToRecordingSettings(ext));
            setContactInfo(mapExtensionToContactInfo(ext));
            setFollowMeData(mapExtensionToFollowMe(ext));
            setPersonalAssistantData(mapExtensionToPersonalAssistant(ext));

            // Merge device fields (only for SIP type)
            const devicePatch = mapExtensionToDeviceData(ext);
            if (Object.keys(devicePatch).length > 0) {
                setFormData(prev => ({ ...prev, ...devicePatch }));
            }
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Trigger load on id / duplicateId change
    useEffect(() => {
        if (isEditMode && id && id !== 'new') {
            loadExtension(id);
        } else if (!isEditMode && duplicateId) {
            loadExtension(duplicateId, true);
        }
    }, [id, isEditMode, duplicateId, loadExtension]);

    // ── New-form reset + system defaults ────────────────────
    useEffect(() => {
        const currentId = id || 'new';
        const savedDataKey = 'extensions:saveAndNewData';
        const locationKey = location.key || location.pathname + location.search;

        if (!isEditMode) {
            const resetTimestamp = sessionStorage.getItem(savedDataKey + ':reset');
            const lastResetTimestamp = lastLoadedIdRef.current === 'new'
                ? sessionStorage.getItem(savedDataKey + ':lastReset') : null;

            const shouldReset =
                lastLoadedIdRef.current !== currentId ||
                lastLocationKeyRef.current !== locationKey ||
                (lastLoadedIdRef.current === 'new' && currentId === 'new' &&
                    resetTimestamp && resetTimestamp !== lastResetTimestamp);

            if (shouldReset) {
                let preservedFormData: Partial<ExtensionFormState> = {};
                let preservedVoicemailData: Partial<VoicemailSettingsState> = {};
                
                try {
                    const stored = sessionStorage.getItem(savedDataKey);
                    if (stored) {
                        const parsed = JSON.parse(stored);
                        if (parsed.formData) {
                            // Preserve sticky fields across Save & New actions
                            preservedFormData = {
                                classOfServicesId: parsed.formData.classOfServicesId,
                                languageCode: parsed.formData.languageCode,
                                voice: parsed.formData.voice,
                                soundPrefix: parsed.formData.soundPrefix,
                                deviceSipProfileId: parsed.formData.deviceSipProfileId,
                                deviceDtmfMode: parsed.formData.deviceDtmfMode,
                                deviceMaxContacts: parsed.formData.deviceMaxContacts,
                                deviceInboundCodecs: parsed.formData.deviceInboundCodecs,
                                deviceOutboundCodecs: parsed.formData.deviceOutboundCodecs
                            };
                        }
                        if (parsed.voicemailData) {
                            preservedVoicemailData = {
                                language: parsed.voicemailData.language
                            };
                        }
                    }
                    sessionStorage.removeItem(savedDataKey);
                    if (resetTimestamp) {
                        sessionStorage.setItem(savedDataKey + ':lastReset', resetTimestamp);
                        sessionStorage.removeItem(savedDataKey + ':reset');
                    }
                } catch { /* ignore */ }

                setFormData({ ...createDefaultFormData(), ...preservedFormData });
                setVoicemailData({ ...createDefaultVoicemailData(), ...preservedVoicemailData });
                setRecordingSettings(createDefaultRecordingSettings());
                setContactInfo(createDefaultContactInfo());
                setFollowMeData(createDefaultFollowMeData());
                setPersonalAssistantData(createDefaultPersonalAssistantData());

                lastLoadedIdRef.current = currentId;
                lastLocationKeyRef.current = locationKey;

                // Load system defaults
                const loadDefaults = async () => {
                    try {
                        if (selectedDomainId && typeof selectedDomainId === 'number') {
                            const defaults = await fetchSystemDefaults(selectedDomainId) as SystemDefaults;
                            if (defaults) {
                                const { languageCode: defaultLanguageCode, voice: derivedVoice } = parseLanguagePayload(defaults.defaultLanguage);
                                const normalizedVoice = defaults.defaultVoice || derivedVoice;
                                const voicemailLanguage = getDefaultVoicemailLanguage(defaultLanguageCode);
                                const defaultSoundPrefix = buildSoundPrefixPath(defaultLanguageCode, normalizedVoice);

                                setFormData(prev => ({
                                    ...prev,
                                    languageCode: defaultLanguageCode || prev.languageCode,
                                    voice: normalizedVoice || prev.voice,
                                    soundPrefix: prev.soundPrefix || defaultSoundPrefix,
                                    voicemailEnabled: defaults.voicemailEnabled ?? prev.voicemailEnabled,
                                    callTimeout: defaults.ringTimeout || prev.callTimeout,
                                    maxCalls: defaults.maxCalls ?? prev.maxCalls,
                                    callWaiting: defaults.callWaiting ?? prev.callWaiting,
                                    rejectAnonymous: defaults.rejectAnonymous ?? prev.rejectAnonymous,
                                    holdMusic: defaults.holdMusic || prev.holdMusic,
                                    privacy: defaults.privacy || prev.privacy,
                                    callScreen: defaults.callScreen ?? prev.callScreen,
                                    deviceMaxContacts: defaults.maxRegistrations?.toString() || prev.deviceMaxContacts,
                                    deviceInboundCodecs: defaults.inboundCodecPrefs || prev.deviceInboundCodecs,
                                    deviceOutboundCodecs: defaults.outboundCodecPrefs || prev.deviceOutboundCodecs,
                                    deviceDtmfMode: defaults.dtmfMode || prev.deviceDtmfMode,
                                }));

                                if (defaults.voicemailEnabled) {
                                    setVoicemailData(prev => ({
                                        ...prev,
                                        maxMessages: defaults.voicemailMaxMessages || prev.maxMessages,
                                        notificationEnabled: defaults.voicemailNotificationEnabled ?? prev.notificationEnabled,
                                        transcribeEnabled: defaults.voicemailTranscribeEnabled ?? prev.transcribeEnabled,
                                        emailAttachment: defaults.voicemailEmailAttachment ?? prev.emailAttachment,
                                        language: voicemailLanguage || DEFAULT_LANGUAGE_CODE || prev.language,
                                        timezone: defaults.defaultTimezone || prev.timezone,
                                    }));
                                }

                                setRecordingSettings(prev => ({
                                    ...prev,
                                    recordIncoming: defaults.recordIncoming ?? prev.recordIncoming,
                                    recordOutgoing: defaults.recordOutgoing ?? prev.recordOutgoing,
                                    recordInternal: defaults.recordInternal ?? prev.recordInternal,
                                    recordOnDemand: defaults.recordOnDemand ?? prev.recordOnDemand,
                                    recordingFormat: defaults.recordingFormat || prev.recordingFormat,
                                }));
                            }
                        }

                        const nextExtension = await fetchNextExtensionNumber(selectedDomainId);
                        if (nextExtension) {
                            setFormData(prev => ({ ...prev, extension: nextExtension }));
                        }
                    } catch { /* use defaults from formDefaults */ }
                };

                loadDefaults();
            }
        } else if (isEditMode && id && id !== 'new') {
            lastLoadedIdRef.current = id;
            lastLocationKeyRef.current = location.key || location.pathname + location.search;
        }
    }, [isEditMode, selectedDomainId, id, location.key, location.pathname, location.search]);

    return {
        formData,
        setFormData,
        voicemailData,
        setVoicemailData,
        recordingSettings,
        setRecordingSettings,
        contactInfo,
        setContactInfo,
        followMeData,
        setFollowMeData,
        personalAssistantData,
        setPersonalAssistantData,
        options,
        isLoading,
        isLoadingOptions,
    };
}
